import { useState } from 'react'
import { LuMinimize2, LuPencil, LuSearch, LuX } from 'react-icons/lu'

import { useTagsStore } from '@/feature/tags/store'
import { useTagToggle } from '@/feature/tags/hooks'

import { Badge, BadgeVariant } from '@/lib/core/components/Badge'
import { Truncate } from '@/lib/core/components/Truncate'

import styles from './TagsOverviewPanel.module.css'

export const TagsOverviewPanel = () => {
  const {
    globalOrgTags,
    isModalOpen,
    closeModal,
    activeAssetData,
    openPopout,
    popoverPosition,
    isTagsUpdating,
  } = useTagsStore()

  const [searchTerm, setSearchTerm] = useState('')

  // Use the shared hook for tag toggling
  const { handleTagToggle } = useTagToggle()

  // Handle edit tag - open popout in edit mode
  const handleEditTag = () => {
    // Close the modal first
    closeModal()

    // Open popout in edit mode at center of screen
    const centerX = window.innerWidth / 2
    const centerY = window.innerHeight / 2

    openPopout({
      position: { x: centerX, y: centerY },
      activeAssetData,
    })

    // Note: The actual edit mode switching will be handled by the popout component
    // We could extend the store to support opening directly in edit mode if needed
  }

  // Handle backdrop click to close the modal
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if the click was directly on the overlay (not its children)
    if (e.target === e.currentTarget) {
      closeModal()
    }
  }

  // Handle minimize button click to switch back to popout
  const handleMinimize = async () => {
    // First close the modal
    closeModal()

    // Use requestAnimationFrame to ensure the UI has updated before opening the popout
    await new Promise((resolve) => requestAnimationFrame(resolve))

    // If we have a position from before, use it
    if (popoverPosition && activeAssetData) {
      openPopout({
        position: popoverPosition,
        activeAssetData,
      })
    } else {
      // Fallback to center of screen if no position is available
      const centerX = window.innerWidth / 2
      const centerY = window.innerHeight / 2

      openPopout({
        position: { x: centerX, y: centerY },
        activeAssetData,
      })
    }
  }

  if (!isModalOpen) return null

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Manage Tags for Asset ID {activeAssetData?.id}</h2>
          <div className={styles.headerActions}>
            <button className={styles.minimizeButton} onClick={handleMinimize}>
              <LuMinimize2 size={16} />
            </button>
            <button className={styles.closeButton} onClick={closeModal}>
              <LuX size={16} />
            </button>
          </div>
        </div>

        <div className={styles.searchSection}>
          <div className={styles.searchContainer}>
            <LuSearch className={styles.searchIcon} size={16} />
            <input
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <button className={styles.createLabelButton}>Create New Tag</button>
        </div>

        <div className={styles.tagsGrid}>
          {globalOrgTags.map((tag) => {
            const tagId = tag.id.toString()
            // Use local state for rendering
            const isSelected =
              activeAssetData?.tags &&
              activeAssetData?.tags.map((t) => t.id.toString()).includes(tagId)
            return (
              <div
                key={tagId}
                className={`${styles.tagItem} ${isSelected ? styles.tagItemSelected : ''}`}
              >
                <div className={styles.leftSide}>
                  <div className={styles.tagCheckbox}>
                    <input
                      type="checkbox"
                      id={`tag-${tagId}`}
                      checked={isSelected}
                      onChange={() => handleTagToggle(tag, isSelected)}
                      disabled={isTagsUpdating}
                    />
                    <label
                      htmlFor={`tag-${tagId}`}
                      className={styles.checkboxLabel}
                      style={{
                        backgroundColor: isSelected ? '#314663' : 'transparent',
                        borderColor: isSelected ? '#314663' : '#ccc',
                        opacity: isTagsUpdating ? 0.7 : 1,
                        cursor: isTagsUpdating ? 'wait' : 'pointer',
                      }}
                    >
                      {isSelected && <span className={styles.checkmark}>✓</span>}
                    </label>
                  </div>
                  <Badge
                    variant={BadgeVariant.TAG}
                    backgroundColor={tag.color}
                    minSize={20}
                    onClick={() => handleTagToggle(tag, isSelected)}
                  >
                    <Truncate content={tag.name} limit={12} />
                  </Badge>
                </div>
                <button
                  className={styles.editButton}
                  onClick={() => handleEditTag()}
                  disabled={isTagsUpdating}
                  title="Edit tag"
                >
                  <LuPencil size={16} />
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
